import { createRoutesFromElements, createBrowserRouter, Route } from 'react-router-dom';

// This pages will be imported when they get migrated to the new structure
// import ChangePass from '@/components/ChangePass';
// import KitchenSink from '@/components/KitchenSink';
// import LegalDoc from '@/components/LegalDoc';
// import Users from '@/components/Users';
// import Billing from '@/components/Billing';

// Components or Pages
import { LoginForm } from '@/components/auth/login-form';
import { ChangePass } from '@/components/auth/change-pass';
import { ResetPass } from '@/components/auth/reset-pass';
import { SignUpForm } from '@/components/auth/sign-up';
import { CasesPage } from '@/components/cases/index';
import { NewCasePage } from '@/components/cases/new-case';
import { ProcessCasePage } from '@/components/cases/process-case';
import { CaseEditPage } from '@/components/cases/case-edit';
import { AdminDashboard } from '@/components/admin/dashboard';
import { CasesChartPage } from '@/components/admin/dashboard/cases-chart-page';
import { ProfilePage } from '@/components/pages/profile-page';
import { TwoFactorAuthEnableForm } from '@/components/two-factor-auth/enable-form';
import { TwoFactorAuthDisableForm } from '@/components/two-factor-auth/disable-form';
import { LogoutPage } from '@/components/pages/logout-page';
import { ClinicsAdminPage } from '@/components/admin/clinics';
import { AdminUsersPage } from '@/components/admin/users';
import { ApprovalForm } from '@/components/admin/users/approval-form';
import { UserEditForm } from '@/components/admin/users/user-edit-form';
import { AISearchAdminPage } from '@/components/admin/ai-search';
import { SimpleError } from '@/components/pages/simple-error';
import { ClinicForm } from '@/components/admin/clinics/clicnic-form';
import { SiteForm } from '@/components/admin/clinics/site-form';
import { SiteNPIEdit } from '@/components/admin/clinics/site-npi-edit';
// general pages
import { NotFoundPage } from '@/components/pages/404';
import { TBH } from '@/components/pages/tbh-page';
import { SalesPage } from '@/components/pages/sales-page';
// Layouts for each page we want to render
import { LayoutAuth } from '@/components/auth/layout-auth';
import { MainLayout } from '@/components/main-layout';
import { RouterWrapper } from '@/components/navigation/router-wrapper';
import { InsightsListPage } from '@/components/insights';
import { DrInsightsPage } from '@/components/pages/dr-insights-page';
import { DrInsightsStep1 } from '@/components/insights/dr-insights-flow/step-1';
import { DrInsightsStep2 } from '@/components/insights/dr-insights-flow/step-2';
import { TelehealthReportPrintPage } from '@/components/print/telehealth-report';

// utils
import { REDIRECT_ROUTES, ROUTES } from './routes';
import { authRouteLoader, protectedLoader, redirectHomeLoader } from './loaders';
import { GradingRoutes } from './grading-routes';

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/" element={<RouterWrapper />}>
      {/* AUTH ROUTES */}
      <Route path="auth" element={<LayoutAuth />}>
        <Route path={ROUTES.LOGIN} element={<LoginForm />} loader={authRouteLoader} />
        <Route path={ROUTES.CHANGE_PASSWORD} element={<ChangePass />} loader={authRouteLoader} />
        <Route path={ROUTES.RESET_PASSWORD} element={<ResetPass />} loader={authRouteLoader} />
        <Route path={ROUTES.SIGN_UP} element={<SignUpForm />} loader={authRouteLoader} />
      </Route>
      {/* PRINT ROUTES - standalone, no layout wrapper, no auth loader */}
      <Route path={ROUTES.PRINT_TELEHEALTH_REPORT} element={<TelehealthReportPrintPage />} />
      {/* MAIN ROUTES */}
      <Route path="" element={<MainLayout />}>
        <Route index element={<AdminDashboard />} loader={protectedLoader} />
        <Route
          path={ROUTES.DASHBOARD_CASES_CHART}
          element={<CasesChartPage />}
          loader={protectedLoader}
        />
        <Route path={ROUTES.CASES} element={<CasesPage />} loader={protectedLoader}>
          <Route path={`${ROUTES.NEW_CASE}`} element={<NewCasePage />} />
          <Route path={`${ROUTES.CASE_DETAIL}`} element={<CaseEditPage />} />
          <Route path={`${ROUTES.PROCESS_CASE}`} element={<ProcessCasePage />} />
        </Route>
        {/* GRADING ROUTES */}
        {GradingRoutes}
        {/* INSIGHTS ROUTES */}
        <Route path={ROUTES.INSIGHTS} element={<InsightsListPage />} loader={protectedLoader} />
        <Route path={ROUTES.INSIGHTS_DR_FORM} element={<DrInsightsPage />} loader={protectedLoader}>
          <Route index element={<DrInsightsStep1 />} />
          <Route path="questions" element={<DrInsightsStep1 />} />
          <Route path="follow-up" element={<DrInsightsStep2 />} />
        </Route>
        <Route path={ROUTES.ADMIN}>
          <Route
            path={ROUTES.ADMIN_CLINICS}
            element={<ClinicsAdminPage />}
            errorElement={<SimpleError errorMessage="clinics-error" />}
            loader={protectedLoader}
          >
            <Route path={ROUTES.ADMIN_ADD_CLINIC} element={<ClinicForm />} />
            <Route path={`${ROUTES.ADMIN_EDIT_CLINIC}/:clinicId`} element={<ClinicForm />} />
            <Route path={ROUTES.ADMIN_ADD_CLINIC_SITE} element={<SiteForm />} />
            <Route path={ROUTES.ADMIN_EDIT_CLINIC_SITE} element={<SiteForm />} />
            <Route path={ROUTES.ADMIN_EDIT_SITE_NPI_PREFERRED} element={<SiteNPIEdit />} />
          </Route>
          <Route
            path={ROUTES.ADMIN_USERS}
            element={<AdminUsersPage />}
            errorElement={<SimpleError errorMessage="admin-users-error" />}
            loader={protectedLoader}
          >
            <Route path="approval/:userId" element={<ApprovalForm />} />
            <Route path="edit/:userId" element={<UserEditForm />} />
          </Route>
          <Route path={ROUTES.ADMIN_TELEHEALTH_CLINICIANS} element={<TBH />} />
          <Route path={ROUTES.ADMIN_AI_SEARCH} element={<AISearchAdminPage />} />
        </Route>
        <Route path={ROUTES.PROFILE} element={<ProfilePage />} loader={protectedLoader}>
          <Route
            path={ROUTES.TWO_FACTOR_AUTH_ENABLE}
            element={<TwoFactorAuthEnableForm />}
            loader={protectedLoader}
          />
          <Route
            path={ROUTES.TWO_FACTOR_AUTH_DISABLE}
            element={<TwoFactorAuthDisableForm />}
            loader={protectedLoader}
          />
        </Route>
        <Route path={ROUTES.SALES} element={<SalesPage />} loader={protectedLoader} />
        <Route path={ROUTES.USERS} element={<TBH />} loader={protectedLoader} />
        <Route path={ROUTES.BILLING} element={<TBH />} loader={protectedLoader} />
        <Route path={ROUTES.LEGAL} element={<TBH />} loader={protectedLoader} />
        <Route path={ROUTES.KITCHEN_SINK} element={<TBH />} loader={protectedLoader} />
        <Route path={ROUTES.LOGOUT} element={<LogoutPage />} loader={protectedLoader} />
      </Route>
      {/* Redirect Routes */}
      {REDIRECT_ROUTES.map((route) => (
        <Route key={route} path={route} element={<></>} loader={redirectHomeLoader} />
      ))}
      <Route path="*" element={<NotFoundPage />} />
    </Route>
  )
);
